import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  RefreshControl,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
  Platform,
} from 'react-native';
import Icon from 'react-native-vector-icons/MaterialIcons';
import {
  ModuleHeader,
  Card,
  Avatar,
  PillButton,
} from '@cubemeet/ui';
import { MODULE_COLORS } from '@cubemeet/core/theme';
import { supabase } from '@cubemeet/core/services/supabase';
import { useTravelI18n } from './useTravelI18n';

interface FriendItem {
  id: string;
  displayName: string;
  avatarUrl?: string | null;
  isMember: boolean;
  hasPendingInvitation: boolean;
}

interface Props {
  groupId: string;
  groupName?: string | null;
  userId: string;
  onBack: () => void;
}

const TravelInviteFriends: React.FC<Props> = ({
  groupId,
  groupName,
  userId,
  onBack,
}) => {
  const { t } = useTravelI18n();
  const [friends, setFriends] = useState<FriendItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [selectedFriends, setSelectedFriends] = useState<Set<string>>(new Set());
  const [inviting, setInviting] = useState(false);

  const showAlert = useCallback((title: string, message?: string) => {
    if (Platform.OS === 'web') {
      if (typeof window !== 'undefined') {
        const text = message ? `${title}\n${message}` : title;
        window.alert(text);
      }
      return;
    }
    // eslint-disable-next-line no-alert
    alert(message ? `${title}\n${message}` : title);
  }, []);

  const loadFriends = useCallback(async () => {
    if (!userId) return;

    try {
      setLoading(true);

      const { data: friendRelations, error: friendsError } = await supabase
        .from('friends')
        .select('friend_id, status')
        .eq('user_id', userId)
        .eq('status', 'active');

      if (friendsError) throw friendsError;

      const friendIds = (friendRelations || [])
        .map((item) => item.friend_id)
        .filter((id): id is string => Boolean(id));

      const friendProfileResult = friendIds.length
        ? await supabase
            .from('profiles')
            .select('id, display_name, avatar_url')
            .in('id', friendIds)
        : ({ data: [] as any[], error: null } as { data: any[]; error: any });

      if (friendProfileResult.error) throw friendProfileResult.error;

      const profileRows = Array.isArray(friendProfileResult.data) ? friendProfileResult.data : [];

      const { data: members, error: membersError } = await supabase
        .from('group_members')
        .select('user_id')
        .eq('group_id', groupId)
        .eq('status', 'active');

      if (membersError) throw membersError;

      const memberIds = (members || []).map((m) => m.user_id);

      const { data: pendingInvitations, error: invitationsError } = await supabase
        .from('group_invitations')
        .select('invitee_id')
        .eq('group_id', groupId)
        .eq('status', 'pending');

      if (invitationsError) throw invitationsError;

      const pendingIds = (pendingInvitations || []).map((inv) => inv.invitee_id);

      const profileMap = new Map<string, { display_name: string; avatar_url?: string | null }>();
      profileRows.forEach((profile: any) => {
        if (profile?.id) {
          profileMap.set(profile.id, {
            display_name: profile.display_name || '',
            avatar_url: profile.avatar_url ?? null,
          });
        }
      });

      const list: FriendItem[] = friendIds.map((id) => {
        const profile = profileMap.get(id);
        return {
          id,
          displayName:
            profile?.display_name || t('inviteFriends.alerts.unknownUser') || '未知用户',
          avatarUrl: profile?.avatar_url,
          isMember: memberIds.includes(id),
          hasPendingInvitation: pendingIds.includes(id),
        };
      });

  list.sort((a, b) => a.displayName.localeCompare(b.displayName, 'zh-Hans'));
      setFriends(list);
    } catch (error: any) {
      console.error('Load friends failed:', error);
      const message =
        (t('inviteFriends.alerts.loadFriendsFailed') || '加载好友失败') +
        (error?.message ? `: ${error.message}` : '');
      showAlert(t('travelDetail.messages.error') || '错误', message);
    } finally {
      setLoading(false);
    }
  }, [groupId, userId, showAlert, t]);

  useEffect(() => {
    loadFriends();
  }, [loadFriends]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await loadFriends();
    setRefreshing(false);
  }, [loadFriends]);

  const toggleSelection = useCallback((friendId: string) => {
    setSelectedFriends((prev) => {
      const next = new Set(prev);
      if (next.has(friendId)) {
        next.delete(friendId);
      } else {
        next.add(friendId);
      }
      return next;
    });
  }, []);

  const filteredFriends = useMemo(() => {
    if (!searchText) return friends;
    const lower = searchText.toLowerCase();
    return friends.filter((friend) => friend.displayName.toLowerCase().includes(lower));
  }, [friends, searchText]);

  const availableFriends = useMemo(
    () => filteredFriends.filter((friend) => !friend.isMember && !friend.hasPendingInvitation),
    [filteredFriends],
  );
  const alreadyMembers = useMemo(
    () => filteredFriends.filter((friend) => friend.isMember),
    [filteredFriends],
  );
  const pendingInvites = useMemo(
    () => filteredFriends.filter((friend) => friend.hasPendingInvitation && !friend.isMember),
    [filteredFriends],
  );

  const sendInvitations = useCallback(async () => {
    if (!userId) {
      showAlert(t('travelDetail.messages.error') || '错误', t('inviteFriends.alerts.userInfoFailed'));
      return;
    }

    if (selectedFriends.size === 0) {
      showAlert(t('travelDetail.messages.error') || '错误', t('inviteFriends.alerts.selectFriends'));
      return;
    }

    try {
      setInviting(true);
      const now = new Date().toISOString();
      const rows = Array.from(selectedFriends).map((friendId) => ({
        group_id: groupId,
        invitee_id: friendId,
        inviter_id: userId,
        status: 'pending',
        created_at: now,
      }));

      const { error } = await supabase.from('group_invitations').insert(rows);
      if (error) throw error;

      const invitedNames = friends
        .filter((friend) => selectedFriends.has(friend.id))
        .map((friend) => friend.displayName)
        .join('、');

      showAlert(
        t('inviteFriends.alerts.invitationSuccess') || '邀请已发送',
        invitedNames
          ? t('inviteFriends.alerts.invitationSuccessMessage', { names: invitedNames }) || invitedNames
          : undefined,
      );
      setSelectedFriends(new Set());
      await loadFriends();
    } catch (error: any) {
      console.error('Send invitations failed:', error);
      const msg =
        (t('inviteFriends.alerts.sendInvitationsFailed') || '发送邀请失败') +
        (error?.message ? `: ${error.message}` : '');
      showAlert(t('travelDetail.messages.error') || '错误', msg);
    } finally {
      setInviting(false);
    }
  }, [friends, groupId, loadFriends, selectedFriends, showAlert, t, userId]);

  const renderFriend = useCallback(
    (friend: FriendItem, options?: { disabled?: boolean; subtitle?: string }) => {
      const disabled = options?.disabled ?? false;
      const subtitle = options?.subtitle;
      const selected = selectedFriends.has(friend.id);

      return (
        <TouchableOpacity
          key={friend.id}
          style={[
            styles.friendItem,
            disabled && styles.friendItemDisabled,
            selected && styles.friendItemSelected,
          ]}
          disabled={disabled}
          onPress={() => toggleSelection(friend.id)}
        >
          <View style={styles.friendInfo}>
            <Avatar
              size="medium"
              avatarUrl={friend.avatarUrl || undefined}
              displayName={friend.displayName}
              style={styles.friendAvatar}
            />
            <View style={styles.friendDetails}>
              <Text style={[styles.friendName, disabled && styles.friendNameDisabled]}>
                {friend.displayName}
              </Text>
              {subtitle ? <Text style={styles.friendSubtitle}>{subtitle}</Text> : null}
            </View>
          </View>
          <View style={styles.friendAction}>
            {disabled ? (
              friend.isMember ? (
                <Icon name="check-circle" size={20} color="#4CAF50" />
              ) : (
                <Icon name="schedule" size={20} color="#FF9800" />
              )
            ) : (
              <View style={[styles.checkbox, selected && styles.checkboxSelected]}>
                {selected ? <Icon name="check" size={16} color="#fff" /> : null}
              </View>
            )}
          </View>
        </TouchableOpacity>
      );
    },
    [selectedFriends, toggleSelection],
  );

  const selectedCount = selectedFriends.size;
  const bottomMessage =
    selectedCount > 0
      ? t('inviteFriends.selection.count', { count: selectedCount }) || `已选择 ${selectedCount} 位好友`
      : t('inviteFriends.alerts.selectFriends') || '选择好友后即可发送邀请';
  const buttonText = inviting
    ? t('inviteFriends.actions.sending') || '发送中…'
    : selectedCount > 0
    ? t('inviteFriends.selection.sendCount', { count: selectedCount }) || `发送邀请 (${selectedCount})`
    : t('inviteFriends.actions.sendInvitations') || '发送邀请';
  const disableSend = selectedCount === 0 || inviting;

  return (
    <View style={styles.container}>
      <ModuleHeader
        moduleType="travel"
        title={t('inviteFriends.header.title') || '邀请好友'}
        subtitle={
          groupName
            ? t('inviteFriends.header.subtitle', { groupName }) || `邀请好友加入 "${groupName}"`
            : undefined
        }
        showMiniCube
        showBackButton
        onBack={onBack}
      />

      <View style={styles.searchContainer}>
        <Card style={styles.searchCard}>
          <View style={styles.searchInner}>
            <Icon name="search" size={20} color="#666" style={styles.searchIcon} />
            <TextInput
              style={styles.searchInput}
              placeholder={t('inviteFriends.search.placeholder') || '搜索好友…'}
              placeholderTextColor="#999"
              value={searchText}
              onChangeText={setSearchText}
            />
            {searchText ? (
              <TouchableOpacity onPress={() => setSearchText('')} style={styles.clearButton}>
                <Icon name="clear" size={20} color="#999" />
              </TouchableOpacity>
            ) : null}
          </View>
        </Card>
      </View>

      <ScrollView
        style={styles.list}
        contentContainerStyle={styles.listContent}
        keyboardShouldPersistTaps="handled"
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={handleRefresh}
            colors={[MODULE_COLORS.travel]}
            tintColor={MODULE_COLORS.travel}
          />
        }
        showsVerticalScrollIndicator={false}
      >
        {loading ? (
          <View style={styles.loadingBlock}>
            <ActivityIndicator size="small" color={MODULE_COLORS.travel} />
            <Text style={styles.loadingText}>{t('inviteFriends.loading.text') || '正在加载好友…'}</Text>
          </View>
        ) : (
          <>
            {availableFriends.length > 0 ? (
              <Card style={styles.section}>
                <Text style={styles.sectionTitle}>
                  {t('inviteFriends.sections.available') || '可邀请的好友'} ({availableFriends.length})
                </Text>
                {availableFriends.map((friend) => renderFriend(friend))}
              </Card>
            ) : null}

            {alreadyMembers.length > 0 ? (
              <Card style={styles.section}>
                <Text style={styles.sectionTitle}>
                  {t('inviteFriends.sections.members') || '已加入的好友'} ({alreadyMembers.length})
                </Text>
                {alreadyMembers.map((friend) =>
                  renderFriend(friend, {
                    disabled: true,
                    subtitle: t('inviteFriends.status.joined') || '已加入',
                  }),
                )}
              </Card>
            ) : null}

            {pendingInvites.length > 0 ? (
              <Card style={styles.section}>
                <Text style={styles.sectionTitle}>
                  {t('inviteFriends.sections.pending') || '待处理邀请'} ({pendingInvites.length})
                </Text>
                {pendingInvites.map((friend) =>
                  renderFriend(friend, {
                    disabled: true,
                    subtitle: t('inviteFriends.status.invitationSent') || '已发送邀请',
                  }),
                )}
              </Card>
            ) : null}

            {!loading && friends.length === 0 ? (
              <Card style={styles.emptyCard}>
                <Icon name="group" size={48} color="#ccc" />
                <Text style={styles.emptyTitle}>
                  {t('inviteFriends.empty.noFriends.title') || '暂无好友'}
                </Text>
                <Text style={styles.emptySubtitle}>
                  {t('inviteFriends.empty.noFriends.subtitle') || '添加好友后再来邀请他们吧'}
                </Text>
              </Card>
            ) : null}

            {friends.length > 0 && filteredFriends.length === 0 ? (
              <Card style={styles.emptyCard}>
                <Icon name="search-off" size={48} color="#ccc" />
                <Text style={styles.emptyTitle}>
                  {t('inviteFriends.empty.noResults.title') || '没有匹配的结果'}
                </Text>
                <Text style={styles.emptySubtitle}>
                  {t('inviteFriends.empty.noResults.subtitle') || '试试调整关键字'}
                </Text>
              </Card>
            ) : null}
          </>
        )}
      </ScrollView>

      <View style={styles.bottomBar}>
        <Text style={styles.bottomText}>{bottomMessage}</Text>
        <PillButton
          text={buttonText}
          iconName="send"
          onPress={sendInvitations}
          disabled={disableSend}
          style={styles.bottomButton}
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  searchContainer: {
    paddingHorizontal: 24,
    paddingBottom: 16,
  },
  searchCard: {
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  searchInner: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  searchIcon: {
    marginRight: 12,
  },
  clearButton: {
    padding: 4,
    marginLeft: 8,
  },
  searchInput: {
    flex: 1,
    fontSize: 16,
    color: '#111827',
    paddingVertical: 4,
  },
  list: {
    flex: 1,
  },
  listContent: {
    paddingHorizontal: 24,
    paddingBottom: 24,
     flexGrow: 1,
  },
  section: {
    padding: 16,
    borderRadius: 20,
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: '#111827',
    marginBottom: 4,
  },
  friendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 12,
    paddingHorizontal: 4,
  },
  friendItemDisabled: {
    opacity: 0.6,
  },
  friendItemSelected: {
    backgroundColor: '#F3F4F6',
    borderRadius: 16,
    paddingHorizontal: 12,
  },
  friendInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    marginRight: 12,
  },
  friendAvatar: {
    backgroundColor: '#E5E7EB',
    marginRight: 12,
  },
  friendDetails: {
    flex: 1,
  },
  friendName: {
    fontSize: 15,
    color: '#111827',
    fontWeight: '600',
  },
  friendNameDisabled: {
    color: '#6B7280',
  },
  friendSubtitle: {
    marginTop: 2,
    fontSize: 13,
    color: '#6B7280',
  },
  friendAction: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  checkbox: {
    width: 24,
    height: 24,
    borderRadius: 12,
    borderWidth: 2,
    borderColor: MODULE_COLORS.travel,
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkboxSelected: {
    backgroundColor: MODULE_COLORS.travel,
  },
  loadingBlock: {
    alignItems: 'center',
    paddingVertical: 40,
    paddingHorizontal: 16,
  },
  loadingText: {
    fontSize: 14,
    color: '#4B5563',
    marginTop: 12,
  },
  emptyCard: {
    alignItems: 'center',
    paddingVertical: 36,
    paddingHorizontal: 24,
    marginBottom: 16,
  },
  emptyTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#111827',
    marginTop: 8,
  },
  emptySubtitle: {
    fontSize: 13,
    color: '#6B7280',
    textAlign: 'center',
    paddingHorizontal: 16,
    marginTop: 4,
  },
  bottomBar: {
    paddingHorizontal: 24,
    paddingVertical: 16,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: '#E5E7EB',
    backgroundColor: '#FFFFFF',
  },
  bottomText: {
    fontSize: 14,
    color: '#4B5563',
    marginBottom: 12,
  },
  bottomButton: {
    alignSelf: 'flex-end',
  },
});

export default TravelInviteFriends;
